import { useState, useEffect } from "react";
import {
  Upload,
  Trash2,
  Star,
  ArrowLeft,
  Save,
  Home,
  FileText,
  ImagePlus,
  CheckSquare,
  Tag,
} from "lucide-react";
import { useHouseDesign, useBuyPlans } from "../../useHook";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { sanitizePositionInput } from "../../../../Utils/positionInput";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../Components/Shared/ImageSeoFields";
import { RichTextEditor } from "../../../../Components/Shared/RichTextEditor";
import {
  ASSOCIATED_HOUSE_OPTIONS,
  STANDARD_PLAN_BAND_OPTIONS,
  urduFor,
} from "../../../../Utils/staticDropdownOptions";

/** Shared create/edit form — uses RichTextEditor (not ReactQuill). */
const emptyFormData = {
  title: "",
  title_ur: "",
  plot_dimension: "",
  covered_area: "",
  floors: 0,
  bed_rooms: 0,
  bath_rooms: 0,
  associated_house: "",
  associated_house_ur: "",
  standard_plan_bands: "",
  standard_plan_band_ur: "",
  customUrl: "",
  description: "",
  description_ur: "",
  features_ur: "",
  servant_quarters: false,
  terrace: false,
  car_parking: false,
  custom_pricing_plan: false,
  selected_buy_plan_ids: [],
  images: [],
  imagePreviews: [],
  imagePositions: [],
  imageAlts: [],
  imageTitles: [],
  primaryImageIndex: 0,
};

const cardIcon =
  "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]";
const featureChip =
  "flex items-center gap-2 rounded-xl border border-[#15173d]/10 bg-[#faf8fa] p-3 cursor-pointer transition hover:border-[#982598]/35 hover:bg-[#982598]/5";

export const HouseDesignForm = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const {
    addHouseDesign,
    updateHouseDesign,
    getHouseDesignById,
    getAssociatedPlan,
    updateHouseDesignPrimaryImage,
  } = useHouseDesign();
  const { getPlans } = useBuyPlans();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [buyPlansList, setBuyPlansList] = useState([]);
  const [formData, setFormData] = useState({ ...emptyFormData });

  useEffect(() => {
    if (formData.custom_pricing_plan) {
      getPlans(setBuyPlansList);
    }
  }, [formData.custom_pricing_plan]);

  useEffect(() => {
    if (isEditMode && editId) {
      loadDesignForEdit(editId);
    }
  }, [isEditMode, editId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue };
      if (name === "associated_house") {
        next.associated_house_ur = urduFor(ASSOCIATED_HOUSE_OPTIONS, nextValue);
      }
      if (name === "standard_plan_bands") {
        next.standard_plan_band_ur = urduFor(
          STANDARD_PLAN_BAND_OPTIONS,
          nextValue,
        );
      }
      return next;
    });
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const handleDescriptionUrChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description_ur: value,
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newValidImages = [];
    const newImagePreviews = [];

    const checkImageResolution = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width === 1600 && img.height === 900) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            alert(
              `Image "${files[index].name}" does not have the required resolution of 1600*900. Please upload the correct resolution.`,
            );
          }
        });
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex =
            currentImageCount === 0 ? 0 : prev.primaryImageIndex;
          const combinedCount = currentImageCount + newValidImages.length;
          let next = 1;
          const imagePositions = Array.from({ length: combinedCount }, (_, i) =>
            i === newPrimaryIndex ? "" : String(next++),
          );

          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            imageAlts: [
              ...(prev.imageAlts || []),
              ...newValidImages.map(() => ""),
            ],
            imageTitles: [
              ...(prev.imageTitles || []),
              ...newValidImages.map(() => ""),
            ],
            imagePositions,
            primaryImageIndex: newPrimaryIndex,
          };
        });
      },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith("/")) {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if (
      (!formData.images || formData.images.length === 0) &&
      !(isEditMode && existingImages.length > 0)
    ) {
      setImageError("At least one image is required");
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (
          key !== "image" &&
          key !== "images" &&
          key !== "imagePreviews" &&
          key !== "imagePositions" &&
          key !== "imageAlts" &&
          key !== "imageTitles" &&
          key !== "primaryImageIndex" &&
          key !== "selected_buy_plan_ids"
        ) {
          apiData.append(key, formData[key]);
        }
      });

      if (formData.selected_buy_plan_ids?.length > 0) {
        apiData.append(
          "selected_buy_plan_ids",
          JSON.stringify(formData.selected_buy_plan_ids),
        );
      }

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          const isPrimary = index === formData.primaryImageIndex;
          apiData.append(`isPrimary`, isPrimary);
          const position =
            formData.imagePositions?.[index] != null
              ? formData.imagePositions[index]
              : index + 1;
          apiData.append(`imagePosition`, position);
          const alt = formData.imageAlts?.[index];
          const title = formData.imageTitles?.[index];
          if (alt != null && String(alt).trim()) {
            apiData.append("imageAlt", String(alt).trim());
          }
          if (title != null && String(title).trim()) {
            apiData.append("imageTitle", String(title).trim());
          }
        });
      }

      if (isEditMode && existingImages.length > 0) {
        existingImages.forEach((image) => {
          apiData.append(
            "existing_images",
            JSON.stringify({
              ...image,
              image_alt: image.image_alt || "",
              image_title: image.image_title || "",
            }),
          );
        });
      }

      let response;
      if (isEditMode) {
        response = await updateHouseDesign(editId, apiData);
      } else {
        response = await addHouseDesign(apiData);
      }

      if (response && response.success === false) {
        throw new Error(response.message || "Failed to save house design.");
      }

      setExistingImages([]);
      resetForm();

      Swal.fire({
        icon: "success",
        title: isEditMode ? "House Design Updated" : "House Design Added",
        text: response?.message,
        showConfirmButton: false,
        timer: 1500,
      });

      navigate("/house-design");
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message || "Failed to save house design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }

    setFormData({ ...emptyFormData, name: "" });
  };

  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    const updatedPreviews = [...(formData.imagePreviews || [])];
    const updatedPositions = [...(formData.imagePositions || [])];
    const updatedAlts = [...(formData.imageAlts || [])];
    const updatedTitles = [...(formData.imageTitles || [])];

    if (updatedPreviews[index]) {
      URL.revokeObjectURL(updatedPreviews[index]);
    }

    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);
    updatedPositions.splice(index, 1);
    updatedAlts.splice(index, 1);
    updatedTitles.splice(index, 1);

    let newPrimaryIndex = formData.primaryImageIndex;
    if (index <= formData.primaryImageIndex) {
      newPrimaryIndex = Math.max(0, formData.primaryImageIndex - 1);
    }

    setFormData({
      ...formData,
      images: updatedImages,
      imagePreviews: updatedPreviews,
      imagePositions: updatedPositions,
      imageAlts: updatedAlts,
      imageTitles: updatedTitles,
      primaryImageIndex: newPrimaryIndex,
    });
    if (updatedImages.length === 0) {
      setImageError("At least one image is required");
    }
  };

  const onImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const images = [...(prev.images || [])];
      const positions = images.map(
        (_, i) => prev.imagePositions?.[i] ?? i + 1,
      );
      positions[index] = cleaned;
      return { ...prev, imagePositions: positions };
    });
  };

  const setPrimaryImage = (index) => {
    setFormData((prev) => {
      const images = prev.images || [];
      let next = 1;
      const imagePositions = images.map((_, i) =>
        i === index ? "" : String(next++),
      );
      return {
        ...prev,
        primaryImageIndex: index,
        imagePositions,
      };
    });
  };

  const onExistingImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onExistingImageSeoChange = (index, field, value) => {
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const onNewImageSeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "imageAlts" : "imageTitles";
    setFormData((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const handleSetExistingPrimary = async (imageId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to change the primary image of this house design?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!",
    });

    if (!result.isConfirmed) return;

    try {
      await updateHouseDesignPrimaryImage(imageId);
      setExistingImages((prev) => {
        let nextIndex = 1;
        return prev.map((img) => {
          if (img.id === imageId) {
            return { ...img, is_primary: true, position: "" };
          }
          return {
            ...img,
            is_primary: false,
            position: nextIndex++,
          };
        });
      });
      Swal.fire({
        title: "Success",
        text: "Primary image has been updated",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update primary image:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update primary image. Please try again.",
        icon: "error",
      });
    }
  };

  const loadDesignForEdit = async (id) => {
    try {
      setIsSubmitting(true);
      const design = await getHouseDesignById(id);
      if (!design) {
        throw new Error("House design not found");
      }

      const toPlanId = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      let selectedPlanIds = (
        Array.isArray(design.selected_buy_plan_ids)
          ? design.selected_buy_plan_ids
          : Array.isArray(design.buyPlans)
            ? design.buyPlans.map((plan) => plan.id || plan.buy_plan_id)
            : Array.isArray(design.buyOptions)
              ? design.buyOptions.map(
                  (opt) => opt.buy_plan_id || opt.buyPlan?.id,
                )
              : []
      )
        .map(toPlanId)
        .filter((planId) => planId != null);

      if (selectedPlanIds.length === 0) {
        try {
          const associated = await getAssociatedPlan(() => {}, id);
          const rows = Array.isArray(associated?.data) ? associated.data : [];
          selectedPlanIds = rows
            .map((row) => row.buy_plan_id || row.buyPlan?.id || row.id)
            .map(toPlanId)
            .filter((planId) => planId != null);
        } catch (assocError) {
          console.warn("Failed to load buy plan associations:", assocError);
        }
      }

      setFormData({
        title: design.title || design.name || "",
        title_ur: design.title_ur || "",
        plot_dimension: design.plot_dimension || "",
        covered_area: design.covered_area || "",
        floors: design.floors || 0,
        bed_rooms: design.bed_rooms || 0,
        bath_rooms: design.bath_rooms || 0,
        associated_house: design.associated_house || "",
        associated_house_ur: design.associated_house_ur || "",
        standard_plan_bands:
          design.standard_plan_bands || design.standard_plan_band || "",
        standard_plan_band_ur: design.standard_plan_band_ur || "",
        customUrl: design.customUrl || "",
        description: design.description || "",
        description_ur: design.description_ur || "",
        features_ur: design.features_ur || "",
        servant_quarters: !!design.servant_quarters,
        terrace: !!design.terrace,
        car_parking: !!design.car_parking,
        custom_pricing_plan:
          !!design.custom_pricing_plan || selectedPlanIds.length > 0,
        selected_buy_plan_ids: selectedPlanIds,
        images: [],
        imagePreviews: [],
        imageAlts: [],
        imageTitles: [],
        primaryImageIndex: 0,
      });
      setExistingImages(
        (design.images || [])
          .map((img, index) => ({
            ...img,
            position: img.is_primary
              ? ""
              : img.position != null && Number(img.position) >= 1
                ? img.position
                : index + 1,
          }))
          .sort((a, b) => {
            if (!!a.is_primary !== !!b.is_primary) return a.is_primary ? -1 : 1;
            return (Number(a.position) || 0) - (Number(b.position) || 0);
          }),
      );
      setImageError("");
    } catch (error) {
      console.error("Failed to load house design for edit:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Failed to load house design for editing.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      if (formData.imagePreviews) {
        formData.imagePreviews.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }
    };
  }, [formData.imagePreviews]);

  const handleFeatureChange = (featureName) => {
    setFormData({
      ...formData,
      [featureName]: !formData[featureName],
    });
  };

  const handleCustomPricingPlanChange = () => {
    setFormData({
      ...formData,
      custom_pricing_plan: !formData.custom_pricing_plan,
      ...(formData.custom_pricing_plan ? { selected_buy_plan_ids: [] } : {}),
    });
  };

  const handleBuyPlanCheckboxChange = (planId) => {
    const normalizedId = Number(planId);
    setFormData((prev) => {
      const ids = (prev.selected_buy_plan_ids || []).map(Number);
      const isSelected = ids.includes(normalizedId);
      return {
        ...prev,
        selected_buy_plan_ids: isSelected
          ? ids.filter((id) => id !== normalizedId)
          : [...ids, normalizedId],
      };
    });
  };

  const handleCancel = () => {
    setExistingImages([]);
    resetForm();
    navigate("/house-design");
  };

  const renderActionButtons = () => (
    <>
      <button
        type="button"
        onClick={handleCancel}
        className="ui-btn-cancel"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="ui-btn-save"
        disabled={isSubmitting}
      >
        <Save size={14} />
        {isSubmitting
          ? isEditMode
            ? "Updating..."
            : "Saving..."
          : isEditMode
            ? "Update"
            : "Save"}
      </button>
    </>
  );

  return (
    <div className="house-design-form-page min-h-0">
      <form onSubmit={handleSubmit}>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="ui-btn-back"
              aria-label="Back"
              disabled={isSubmitting}
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <p className="mb-0 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#982598]">
                House Design
              </p>
              <h2 className="text-xl font-extrabold tracking-tight text-[#15173d]">
                {isEditMode ? "Edit House Design" : "Add House Design"}
              </h2>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#15173d]/10 bg-white px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-[#15173d]">
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  isEditMode ? "bg-[#982598]" : "bg-green-600"
                }`}
              />
              {isEditMode ? "Edit" : "Create"}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Basic details */}
          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <Home size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Basic details
                </h3>
                <p className="text-[11px] text-gray-500">
                  Title, dimensions, categories &amp; URL
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="ui-form-label">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
                <UrduInput
                  label="عنوان"
                  name="title_ur"
                  value={formData.title_ur}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="ui-form-label">Plot Dimension</label>
                <input
                  type="text"
                  name="plot_dimension"
                  value={formData.plot_dimension}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">Covered Area</label>
                <input
                  type="text"
                  name="covered_area"
                  value={formData.covered_area}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">Floors</label>
                <input
                  type="number"
                  name="floors"
                  value={formData.floors}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">Bedrooms</label>
                <input
                  type="number"
                  name="bed_rooms"
                  value={formData.bed_rooms}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">Bathrooms</label>
                <input
                  type="number"
                  name="bath_rooms"
                  value={formData.bath_rooms}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">
                  Associate to category (English)
                </label>
                <select
                  name="associated_house"
                  value={formData.associated_house}
                  onChange={handleChange}
                  className="ui-select-field"
                  required
                >
                  <option value="">Select category</option>
                  {ASSOCIATED_HOUSE_OPTIONS.map((o) => (
                    <option key={o.en} value={o.en}>
                      {o.en === "others" ? "Others" : o.en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ui-form-label">زمرہ سے منسلک کریں</label>
                <select
                  name="associated_house_ur"
                  value={formData.associated_house_ur}
                  onChange={handleChange}
                  dir="rtl"
                  className="ui-select-field"
                >
                  <option value="">منتخب کریں...</option>
                  {ASSOCIATED_HOUSE_OPTIONS.map((o) => (
                    <option key={o.ur} value={o.ur}>
                      {o.ur}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ui-form-label">
                  Standard Plan Bands (English)
                </label>
                <select
                  name="standard_plan_bands"
                  value={formData.standard_plan_bands}
                  onChange={handleChange}
                  className="ui-select-field"
                >
                  <option value="">Select Standard Plan Band</option>
                  {STANDARD_PLAN_BAND_OPTIONS.map((o) => (
                    <option key={o.en} value={o.en}>
                      {o.en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ui-form-label">معیاری پلان بینڈز</label>
                <select
                  name="standard_plan_band_ur"
                  value={formData.standard_plan_band_ur}
                  onChange={handleChange}
                  dir="rtl"
                  className="ui-select-field"
                >
                  <option value="">منتخب کریں...</option>
                  {STANDARD_PLAN_BAND_OPTIONS.map((o) => (
                    <option key={o.ur} value={o.ur}>
                      {o.ur}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ui-form-label">
                  Custom Url <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customUrl"
                  value={formData.customUrl}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <Tag size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Pricing
                </h3>
                <p className="text-[11px] text-gray-500">
                  Optional custom buy plans
                </p>
              </div>
            </div>

            <div
              className={`${featureChip} w-fit`}
              onClick={handleCustomPricingPlanChange}
            >
              <input
                type="checkbox"
                id="custom_pricing_plan"
                name="custom_pricing_plan"
                checked={formData.custom_pricing_plan}
                onChange={handleCustomPricingPlanChange}
                className="h-4 w-4 accent-[#982598]"
              />
              <label
                htmlFor="custom_pricing_plan"
                className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                onClick={(e) => e.preventDefault()}
              >
                Custom Pricing Plan
              </label>
            </div>

            {formData.custom_pricing_plan && (
              <div className="mt-4 rounded-xl border border-[#15173d]/10 bg-[#faf8fa] p-4">
                <h3 className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#15173d]/70">
                  Select Buy Plans
                </h3>
                <div className="max-h-48 space-y-2 overflow-y-auto">
                  {buyPlansList.length === 0 ? (
                    <p className="text-sm text-gray-500">Loading buy plans...</p>
                  ) : (
                    buyPlansList.map((plan) => {
                      const planId = Number(plan.id);
                      const isChecked = (
                        formData.selected_buy_plan_ids || []
                      ).some((id) => Number(id) === planId);
                      return (
                        <div
                          key={plan.id}
                          className={featureChip}
                          onClick={() => handleBuyPlanCheckboxChange(planId)}
                        >
                          <input
                            type="checkbox"
                            id={`buy-plan-${plan.id}`}
                            checked={isChecked}
                            onChange={() =>
                              handleBuyPlanCheckboxChange(planId)
                            }
                            className="h-4 w-4 accent-[#982598]"
                          />
                          <label
                            htmlFor={`buy-plan-${plan.id}`}
                            className="flex-1 cursor-pointer select-none text-sm text-[#15173d]"
                            onClick={(e) => e.preventDefault()}
                          >
                            {plan.name || `Plan #${plan.id}`}
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Description */}
          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <FileText size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Description
                </h3>
                <p className="text-[11px] text-gray-500">
                  English &amp; Urdu content
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="ui-form-label">Description</label>
                <RichTextEditor
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="Write house design description..."
                  minHeight={180}
                  maxHeight={420}
                />
              </div>
              <div>
                <label className="ui-form-label text-right" dir="rtl">
                  تفصیل
                </label>
                <RichTextEditor
                  dir="rtl"
                  value={formData.description_ur}
                  onChange={handleDescriptionUrChange}
                  placeholder="اردو میں تفصیل لکھیں..."
                  minHeight={180}
                  maxHeight={420}
                />
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <CheckSquare size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Features
                </h3>
                <p className="text-[11px] text-gray-500">
                  Amenities in English &amp; Urdu
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="ui-form-label">Features</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div
                    className={featureChip}
                    onClick={() => handleFeatureChange("servant_quarters")}
                  >
                    <input
                      type="checkbox"
                      id="servant_quarters_en"
                      name="servant_quarters"
                      checked={formData.servant_quarters}
                      onChange={() => handleFeatureChange("servant_quarters")}
                      className="h-4 w-4 accent-[#982598]"
                    />
                    <label
                      htmlFor="servant_quarters_en"
                      className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                      onClick={(e) => e.preventDefault()}
                    >
                      Servant Quarters
                    </label>
                  </div>
                  <div
                    className={featureChip}
                    onClick={() => handleFeatureChange("terrace")}
                  >
                    <input
                      type="checkbox"
                      id="terrace_en"
                      name="terrace"
                      checked={formData.terrace}
                      onChange={() => handleFeatureChange("terrace")}
                      className="h-4 w-4 accent-[#982598]"
                    />
                    <label
                      htmlFor="terrace_en"
                      className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                      onClick={(e) => e.preventDefault()}
                    >
                      Terrace
                    </label>
                  </div>
                  <div
                    className={featureChip}
                    onClick={() => handleFeatureChange("car_parking")}
                  >
                    <input
                      type="checkbox"
                      id="car_parking_en"
                      name="car_parking"
                      checked={formData.car_parking}
                      onChange={() => handleFeatureChange("car_parking")}
                      className="h-4 w-4 accent-[#982598]"
                    />
                    <label
                      htmlFor="car_parking_en"
                      className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                      onClick={(e) => e.preventDefault()}
                    >
                      Car Parking
                    </label>
                  </div>
                </div>
              </div>

              <div dir="rtl">
                <h3 className="ui-form-label">خصوصیات</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div
                    className={featureChip}
                    onClick={() => handleFeatureChange("servant_quarters")}
                  >
                    <input
                      type="checkbox"
                      id="servant_quarters_ur"
                      name="servant_quarters"
                      checked={formData.servant_quarters}
                      onChange={() => handleFeatureChange("servant_quarters")}
                      className="h-4 w-4 accent-[#982598]"
                    />
                    <label
                      htmlFor="servant_quarters_ur"
                      className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                      onClick={(e) => e.preventDefault()}
                    >
                      ملازم کا کمرہ
                    </label>
                  </div>
                  <div
                    className={featureChip}
                    onClick={() => handleFeatureChange("terrace")}
                  >
                    <input
                      type="checkbox"
                      id="terrace_ur"
                      name="terrace"
                      checked={formData.terrace}
                      onChange={() => handleFeatureChange("terrace")}
                      className="h-4 w-4 accent-[#982598]"
                    />
                    <label
                      htmlFor="terrace_ur"
                      className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                      onClick={(e) => e.preventDefault()}
                    >
                      چھت
                    </label>
                  </div>
                  <div
                    className={featureChip}
                    onClick={() => handleFeatureChange("car_parking")}
                  >
                    <input
                      type="checkbox"
                      id="car_parking_ur"
                      name="car_parking"
                      checked={formData.car_parking}
                      onChange={() => handleFeatureChange("car_parking")}
                      className="h-4 w-4 accent-[#982598]"
                    />
                    <label
                      htmlFor="car_parking_ur"
                      className="cursor-pointer select-none text-sm font-semibold text-[#15173d]"
                      onClick={(e) => e.preventDefault()}
                    >
                      کار پارکنگ
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Gallery */}
          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <ImagePlus size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Gallery
                </h3>
                <p className="text-[11px] text-gray-500">
                  1600×900 images · set primary &amp; positions
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="ui-form-label">
                Upload Images <span className="text-red-500">*</span>
              </label>
              <div className="group relative rounded-2xl border-2 border-dashed border-[#982598]/35 bg-[rgba(152,37,152,0.03)] p-8 transition hover:border-[#982598] hover:bg-[rgba(152,37,152,0.06)]">
                <input
                  type="file"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="images"
                  accept="image/*"
                />
                <label
                  htmlFor="images"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#982598]/10 text-[#982598]">
                    <Upload size={22} />
                  </span>
                  <span className="text-[13px] font-extrabold text-[#15173d]">
                    Click to upload multiple images
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Only 1600×900 resolution accepted
                  </span>
                </label>
                <div className="pointer-events-none absolute -top-2 left-1/2 z-10 -translate-x-1/2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <div className="whitespace-nowrap rounded-md bg-[#15173d] px-3 py-2 text-xs text-white shadow-lg">
                    Only the image with 1600*900 resolution can be added
                    <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#15173d]"></div>
                  </div>
                </div>
              </div>
            </div>

            {isEditMode && existingImages.length > 0 && (
              <div className="mb-4">
                <h4 className="ui-form-label">
                  Existing Images ({existingImages.length})
                </h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {existingImages.map((image, index) => (
                    <div key={image.id || index} className="group relative">
                      <div
                        className={`relative flex h-24 items-center justify-center overflow-hidden rounded-xl border border-[#15173d]/10 bg-[#faf8fa] ${
                          image.is_primary ? "ring-2 ring-[#982598]" : ""
                        }`}
                      >
                        <img
                          src={image.image_url || image.url || image}
                          alt={`Existing ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        {image.is_primary && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-[#982598] px-1.5 py-0.5 text-[10px] font-bold text-white">
                            PRIMARY
                          </div>
                        )}
                      </div>

                      <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => handleSetExistingPrimary(image.id)}
                          className={`rounded-full p-1 shadow-md ${
                            image.is_primary
                              ? "bg-[#982598] text-white"
                              : "bg-white text-gray-600 hover:bg-[#982598]/10"
                          }`}
                          title={
                            image.is_primary
                              ? "Primary Image"
                              : "Set as Primary"
                          }
                        >
                          <Star
                            size={12}
                            fill={image.is_primary ? "currentColor" : "none"}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setExistingImages((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                          className="rounded-full bg-red-500 p-1 text-white"
                          title="Remove Image"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>

                      {!image.is_primary && (
                        <div className="mt-1">
                          <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                            Position
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[1-9][0-9]*"
                            value={image.position ?? ""}
                            onChange={(e) =>
                              onExistingImagePositionChange(
                                index,
                                e.target.value,
                              )
                            }
                            className="ui-input-field w-full px-1 py-1 text-xs"
                            placeholder="1"
                          />
                        </div>
                      )}
                      {image.is_primary && (
                        <p className="mt-1 text-center text-[10px] font-medium text-[#982598]">
                          Primary (not indexed)
                        </p>
                      )}
                      <ImageSeoFields
                        stacked
                        imageAlt={image.image_alt || ""}
                        imageTitle={image.image_title || ""}
                        onAltChange={(value) =>
                          onExistingImageSeoChange(index, "image_alt", value)
                        }
                        onTitleChange={(value) =>
                          onExistingImageSeoChange(index, "image_title", value)
                        }
                        className="mt-2"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.images.length > 0 && (
              <div className="mb-4">
                <h4 className="ui-form-label">
                  Selected Images ({formData.images.length})
                </h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {formData.images.map((file, index) => (
                    <div key={index} className="relative">
                      <div
                        className={`relative flex h-24 items-center justify-center overflow-hidden rounded-xl border border-[#15173d]/10 bg-[#faf8fa] ${
                          index === formData.primaryImageIndex
                            ? "ring-2 ring-[#982598]"
                            : ""
                        }`}
                      >
                        {formData.imagePreviews &&
                        formData.imagePreviews[index] ? (
                          <img
                            src={formData.imagePreviews[index]}
                            alt={file.name || `Image ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <p className="truncate p-2 text-xs text-gray-500">
                            {file.name || "Image " + (index + 1)}
                          </p>
                        )}

                        {index === formData.primaryImageIndex && (
                          <div className="absolute left-1 top-1 rounded-full bg-[#982598] p-1 text-white shadow-md">
                            <Star size={12} fill="white" />
                          </div>
                        )}

                        {index !== formData.primaryImageIndex && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(index)}
                            className="absolute bottom-1 left-1 rounded-full bg-white p-1 text-gray-600 shadow-md transition-colors hover:bg-[#982598]/10"
                            title="Set as Primary Image"
                          >
                            <Star size={12} />
                          </button>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -right-2 -top-2 z-10 rounded-full bg-red-500 p-1 text-white shadow-md transition-colors hover:bg-red-600"
                      >
                        <Trash2 size={12} />
                      </button>

                      {index !== formData.primaryImageIndex ? (
                        <div className="mt-1">
                          <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                            Position
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[1-9][0-9]*"
                            value={formData.imagePositions?.[index] ?? ""}
                            onChange={(e) =>
                              onImagePositionChange(index, e.target.value)
                            }
                            className="ui-input-field w-full px-1 py-1 text-xs"
                            placeholder="1"
                          />
                        </div>
                      ) : (
                        <p className="mt-1 text-center text-[10px] font-medium text-[#982598]">
                          Primary (not indexed)
                        </p>
                      )}
                      <ImageSeoFields
                        stacked
                        imageAlt={formData.imageAlts?.[index] || ""}
                        imageTitle={formData.imageTitles?.[index] || ""}
                        onAltChange={(value) =>
                          onNewImageSeoChange(index, "image_alt", value)
                        }
                        onTitleChange={(value) =>
                          onNewImageSeoChange(index, "image_title", value)
                        }
                        className="mt-2"
                      />
                    </div>
                  ))}
                </div>

                {formData.images.length > 1 && (
                  <div className="mt-3 rounded-xl border border-[#982598]/20 bg-[#982598]/5 p-2.5">
                    <p className="text-xs text-[#982598]">
                      <Star size={12} className="mr-1 inline" />
                      Primary image selected:{" "}
                      {formData.images[formData.primaryImageIndex]?.name ||
                        `Image ${formData.primaryImageIndex + 1}`}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2 border-t border-[#15173d]/[0.07] pt-4">
              {renderActionButtons()}
            </div>
          </section>
        </div>
      </form>
    </div>
  );
};

export default HouseDesignForm;
